import { ExclamationCircleFilled, LeftOutlined, PlusOutlined } from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Col,
	Form,
	Input,
	Popover,
	Radio,
	RadioChangeEvent,
	Row,
	Select,
	Spin,
	Typography
} from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import NumberInput from 'src/components/input-with-number/input-with-number';
import TitleBox from 'src/components/title-box/title-box';
import {
	BORROWER_CREDIT_VALUE,
	DEFAULT_INPUT_MAX_NUMBER,
	HONG_KONG_NATIONALITY_VALUE,
	INTEREST_PAYMENT_MONTH_OPTIONS,
	MONTH_OPTIONS,
	ONBOARDING_CURRENCY_TYPE,
	OTHER_VALUE_TYPE
} from 'src/constants';
import { holdingTypeOptions, OWNERSHIP, USE_OF_FUND, yesNoOptions } from 'src/constants/mortgage-deal';
import { EMAIL_VALIDATION_RULES, ZIPCODE_REGEX } from 'src/constants/validation';
import { scrollPageToTop } from 'src/helpers/window';
import useStore from 'src/hooks/useStore';
import { IMortgageDetailsFormRequest, IReferral } from 'src/interfaces/mortgage-deal/mortgage-deal-details';
import { i18nKey } from 'src/locales/i18n';
import { IMortgageDealDetails } from 'src/store/mortgage-deal/mortgage-deal-details.store';
import { IApplicationStore } from 'src/store/root/application.store';
import styles from './mortgage-deal-details.module.less';

interface IProps {
	isCorporateRequest?: boolean;
	onConfirmForm?: () => void;
	onBack?: () => void;
	isViewFullForm?: boolean;
	cloneMortgageId?: string;
}

const MortgageDealDetails = ({
	onConfirmForm,
	onBack,
	isViewFullForm = false,
	isCorporateRequest = false,
	cloneMortgageId
}: IProps) => {
	const [isDisabledNumberOfBedRoom, setDisabledNumberOfBedroom] = useState(true);

	const [loading, setLoading] = useState(false);

	const addReferralBtn = useRef<HTMLDivElement>(null);

	const mortgageDealDetailsStore: IMortgageDealDetails = useStore('mortgageDealDetails');

	const applicationStore: IApplicationStore = useStore('applicationStore');

	const [searchParams] = useSearchParams();

	const [form] = Form.useForm();

	const [t] = useTranslation();

	const conditionOption = [
		{
			label: t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.new),
			value: 'new'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.liveable),
			value: 'liveable'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.requireRenovation),
			value: 'Require renovation'
	}
];

	const OptionPropertyType = [
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeFlat),
			value: 'Flat/Condo'
		},
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeDetached),
			value: 'Detached'
		},
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeSemiDetached),
			value: 'Semi-Detached'
		},
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeTerracedHouse),
			value: 'Terraced House'
		},
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeHMO),
			value: 'HMO'
		},
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeLand),
			value: 'Land'
		},
		{
			label: t(i18nKey.onboarding.request.loanLendingCriteria.propTypeCommercial),
			value: 'Commercial'
		}
	];

	const numberOfBedroomOption = [
		{
			label: t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.studio),
			value: '0'
		},
		{
			label: '1',
			value: '1'
		},
		{
			label: '2',
			value: '2'
		},
		{
			label: '3+',
			value: '3'
		},
		{
			label: t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.none),
			value: '-1'
		}
	];

	const onChangeReferral = (e: RadioChangeEvent) => {
		if (e.target.value === 'yes') {
			form.setFieldValue(['thirdPartyReferral', 'referralList'], mortgageDealDetailsStore.referralList);
		} else {
			form.setFieldValue(['thirdPartyReferral', 'referralList'], []);
		}

		mortgageDealDetailsStore.updateReferralQuestion(e.target.value);
	};

	const onChangeUseOfFunds = (e: RadioChangeEvent) => {
		mortgageDealDetailsStore.updateUseOfFunds(e.target.value);
		if (e.target.value !== USE_OF_FUND.OTHERS) {
			form.setFieldValue(['loanDetails', 'useOfFundOther'], undefined);
			mortgageDealDetailsStore.clearUseOfFundOther();
		}
	};

	const onChangeOwnership = (e: RadioChangeEvent) => {
		mortgageDealDetailsStore.updateOwnership(e.target.value);
		if (e.target.value !== OWNERSHIP.OTHERS) {
			form.setFieldValue(['securityDetails', 'ownershipOther'], undefined);
			mortgageDealDetailsStore.clearOtherOwnership();
		}
	};

	const onSaveClick = async () => {
		form.submit();
	};

	const onChangePropertyType = (value: string) => {
		if (value === 'HMO' || value == 'Land') {
			setDisabledNumberOfBedroom(false);
			form.setFieldValue(['securityDetails', 'noOfBedroom'], '-1');
		} else {
			setDisabledNumberOfBedroom(true);
			form.setFieldValue(['securityDetails', 'noOfBedroom'], undefined);
		}
	};

	const onFinish = async (values: IMortgageDetailsFormRequest & { mainReferral: IReferral }) => {
		setLoading(true);

		const rs = await mortgageDealDetailsStore.saveMortgageDealDetails({
			...values,
			isCorporateRequest
		});

		if (rs && onConfirmForm) {
			onConfirmForm();
		}
		setLoading(false);
	};

	const onFormFailed = () => {
		const firstInvalidFormItem = form.getFieldsError()[0];

		if (firstInvalidFormItem) {
			form.scrollToField(firstInvalidFormItem.name, {
				behavior: 'smooth'
			});
		}
	};

	const renderOtherUseOfFunds = () => {
		if (mortgageDealDetailsStore.mortgageDealForm.loanDetails?.useOfFunds === OTHER_VALUE_TYPE.OTHER_USE_OF_FUNDS) {
			return (
				<Row>
					<Col span={12}>
						<Radio value={USE_OF_FUND.OTHERS}>
							{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.value.OTHERS)}
						</Radio>
					</Col>
					<Col span={12}>
						<Form.Item
							name={['loanDetails', 'useOfFundOther']}
							required={false}
							rules={[
								{
									required: true,
									message: `${t(i18nKey.onboarding.validation.required)}`
								}
							]}>
							<Input />
						</Form.Item>
					</Col>
				</Row>
			);
		}

		return (
			<>
				<Row>
					<Radio value={USE_OF_FUND.OTHERS}>
						{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.value.OTHERS)}
					</Radio>
				</Row>
			</>
		);
	};

	const renderOwnership = () => {
		if (mortgageDealDetailsStore.mortgageDealForm.securityDetails?.ownership === OTHER_VALUE_TYPE.OTHER_OWNERSHIP) {
			return (
				<Row>
					<Col span={12}>
						<Radio value={OWNERSHIP.OTHERS}>
							{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.ownership.other)}
						</Radio>
					</Col>
					<Col span={12}>
						<Form.Item
							name={['securityDetails', 'ownershipOther']}
							required={false}
							rules={[
								{
									required: true,
									message: `${t(i18nKey.onboarding.validation.required)}`
								}
							]}>
							<Input />
						</Form.Item>
					</Col>
				</Row>
			);
		}

		return (
			<Row>
				<Radio value={OWNERSHIP.OTHERS}>
					{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.ownership.other)}
				</Radio>
			</Row>
		);
	};

	const onChangeValuesForm = (_: string, values: IMortgageDetailsFormRequest & { mainReferral: IReferral }) => {
		if (values.securityDetails.country) {
			mortgageDealDetailsStore.updateCountrySecurityDetails(values.securityDetails.country);
		}

		if (values.solicitorDetails && values.solicitorDetails.country) {
			mortgageDealDetailsStore.updateCountrySolicitorDetails(values.solicitorDetails.country);
		}

		if (values.propertyCompanyManagement && values.propertyCompanyManagement.country) {
			mortgageDealDetailsStore.updateCountryPropertyCompanyManagement(values.propertyCompanyManagement.country);
		}

		if (values.securityDetails) {
			mortgageDealDetailsStore.updateIsSecondMortgageForSecurityQuestion(
				values.securityDetails?.isSecondMortgageForSecurityQuestion
			);

			mortgageDealDetailsStore.updateMortgageApprovedBySecondBankQuestion(
				values.securityDetails?.mortgageApprovedBySecondBankQuestion
			);

			if (!values.securityDetails.mortgageApprovedBySecondBankQuestion) {
				mortgageDealDetailsStore.clearMortgageApprovedBySecondBankQuestionNote();
			}

			if (!values.securityDetails.isSecondMortgageForSecurityQuestion) {
				mortgageDealDetailsStore.clearIsSecondMortgageForSecurityQuestionNote();
			}
		}
	};

	const listProvinceSecurityDetailsOptions = useMemo(() => {
		form.setFieldValue(['securityDetails', 'district'], undefined);

		return applicationStore.provinces
			.filter((province) => province.countryCode === mortgageDealDetailsStore.mortgageDealForm.securityDetails?.country)
			.map((item) => ({
				value: item.id,
				label: item.name
			}));
	}, [mortgageDealDetailsStore.mortgageDealForm.securityDetails?.country]);

	useEffect(() => {
		scrollPageToTop();
	}, []);

	useEffect(() => {
		const mortgageId = searchParams.get('id');

		if (!!mortgageId || !!cloneMortgageId) {
			setLoading(true);
			mortgageDealDetailsStore
				.getMortgageDealDetails(cloneMortgageId! || mortgageId!)
				.then(() => {
					form.setFieldsValue({
						...mortgageDealDetailsStore.mortgageDealForm,
						mainReferral: mortgageDealDetailsStore.mainReferral,
						thirdPartyReferral: {
							referralList: mortgageDealDetailsStore.referralList
						}
					});
					if (
						mortgageDealDetailsStore.mortgageDealForm.thirdPartyReferral &&
						mortgageDealDetailsStore.mortgageDealForm.thirdPartyReferral.length > 0
					) {
						form.setFieldValue('doYouHaveAReferral', 'yes');
						mortgageDealDetailsStore.updateReferralQuestion('yes');
					} else {
						form.setFieldValue('doYouHaveAReferral', 'no');
						mortgageDealDetailsStore.updateReferralQuestion('no');
					}

					setLoading(false);
				})
				.catch();
		}
	}, []);

	return (
		<>
			<Spin size="large" spinning={loading}>
				<Form
					disabled={isViewFullForm}
					form={form}
					layout="vertical"
					onFinishFailed={onFormFailed}
					onValuesChange={onChangeValuesForm}
					initialValues={{
						...mortgageDealDetailsStore.mortgageDealForm,
						mainReferral: mortgageDealDetailsStore.mainReferral,
						thirdPartyReferral: {
							referralList: mortgageDealDetailsStore.referralList
						},
						doYouHaveAReferral: mortgageDealDetailsStore.mortgageDealForm.doYouHaveAReferral
					}}
					onFinish={onFinish}>
					<TitleBox>
						<span>{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.loanDetails)}</span>
					</TitleBox>
					<div className={styles.subForm}>
						<Row>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									required={true}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.borrowAmount)}`}>
									<Row gutter={[16, 16]}>
										<Col span={6}>
											<Form.Item noStyle name={['loanDetails', 'borrowCurrency']}>
												<Select
													options={[
														{
															key: ONBOARDING_CURRENCY_TYPE.HKD,
															value: ONBOARDING_CURRENCY_TYPE.HKD,
															label: ONBOARDING_CURRENCY_TYPE.HKD
														},
														{
															key: ONBOARDING_CURRENCY_TYPE.USD,
															value: ONBOARDING_CURRENCY_TYPE.USD,
															label: ONBOARDING_CURRENCY_TYPE.USD
														},
														{
															key: ONBOARDING_CURRENCY_TYPE.GBP,
															value: ONBOARDING_CURRENCY_TYPE.GBP,
															label: ONBOARDING_CURRENCY_TYPE.GBP
														},
														{
															key: ONBOARDING_CURRENCY_TYPE.JPY,
															value: ONBOARDING_CURRENCY_TYPE.JPY,
															label: ONBOARDING_CURRENCY_TYPE.JPY
														},
														{
															key: ONBOARDING_CURRENCY_TYPE.AUD,
															value: ONBOARDING_CURRENCY_TYPE.AUD,
															label: ONBOARDING_CURRENCY_TYPE.AUD
														},
														{
															key: ONBOARDING_CURRENCY_TYPE.CAD,
															value: ONBOARDING_CURRENCY_TYPE.CAD,
															label: ONBOARDING_CURRENCY_TYPE.CAD
														}
													]}
												/>
											</Form.Item>
										</Col>
										<Col span={18}>
											<Form.Item
												name={['loanDetails', 'borrowAmount']}
												required={false}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}
												normalize={(c: string, p) => {
													if (!c) {
														return '';
													}
													c = c.replaceAll(',', '');
													const newValue = Number(c);

													if (isNaN(newValue) || newValue > DEFAULT_INPUT_MAX_NUMBER) {
														return p || '';
													}
													return newValue.toLocaleString('en-US');
												}}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Col>
								<Form.Item
									name={['loanDetails', 'proposedCurrencies']}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.proposedCurrency)}`}>
									<Checkbox.Group style={{ width: '100%' }}>
										<Row gutter={[24, 16]} align={'middle'} justify="start">
											<Col lg={4} className={styles.alignMiddle}>
												<Checkbox value={'1'}>HKD</Checkbox>
											</Col>
											<Col lg={4} className={styles.alignMiddle}>
												<Checkbox value={'2'}>USD</Checkbox>
											</Col>
											<Col lg={4} className={styles.alignMiddle}>
												<Checkbox value={'3'}>GBP</Checkbox>
											</Col>
											<Col lg={4} className={styles.alignMiddle}>
												<Checkbox value={'4'}>JPY</Checkbox>
											</Col>
											<Col lg={4} className={styles.alignMiddle}>
												<Checkbox value={'5'}>AUD</Checkbox>
											</Col>
											<Col lg={4} className={styles.alignMiddle}>
												<Checkbox value={'6'}>CAD</Checkbox>
											</Col>
										</Row>
									</Checkbox.Group>
								</Form.Item>
							</Col>
						</Row>
						<Row align={'middle'} gutter={[16, 8]}>
							<Col xs={24} sm={24} md={24} lg={10}>
								<Form.Item
									required={true}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.loanTerm)}`}>
									<div className={styles.horizontal}>
										<div className={styles.inlineSelect}>
											<Form.Item
												name={['loanDetails', 'loanTerm']}
												noStyle
												required={false}
												rules={[
													{
														required: true,
														message:`${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Select placeholder={`${t(i18nKey.common.select)}`} options={MONTH_OPTIONS} />
											</Form.Item>
										</div>
										<Typography.Text strong>{t(i18nKey.onboarding.request.loanLendingCriteria.months)}</Typography.Text>
									</div>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={10}>
								<Form.Item
									required={true}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.interestPaymentPeriod.title)}`}>
									<Form.Item
										name={['loanDetails', 'interestPaymentPeriod']}
										noStyle
										required={false}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Radio.Group>
											<Row>
												<Radio value={INTEREST_PAYMENT_MONTH_OPTIONS[0].value}>
													{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.interestPaymentPeriod.value._3months)}
												</Radio>
												<Radio value={INTEREST_PAYMENT_MONTH_OPTIONS[1].value}>
													{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.interestPaymentPeriod.value._6months)}
												</Radio>
											</Row>
										</Radio.Group>
									</Form.Item>
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Form.Item
								style={{ width: '100%' }}
								name={['loanDetails', 'useOfFunds']}
								label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.title)}`}
								required={true}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<Radio.Group className={styles.RadioSpace} onChange={onChangeUseOfFunds}>
									<Row gutter={[0, 10]}>
										<Col xs={24} sm={24} md={24} lg={24} xl={19} xxl={17}>
											<Row gutter={[5, 0]}>
												<Col xs={12} sm={12} md={12} lg={12} xl={6} xxl={6}>
													<Radio value={USE_OF_FUND.NEW_PURCHASE_SELF_USE}>
														{t(
															i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.value.newPurchaseSelfUse
														)}
													</Radio>
												</Col>
												<Col xs={12} sm={12} md={12} lg={12} xl={6} xxl={6}>
													<Radio value={USE_OF_FUND.NEW_PURCHASE_BTL}>
														{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.value.NEW_PURCHASE_BTL)}
													</Radio>
												</Col>
												<Col xs={12} sm={12} md={12} lg={12} xl={6} xxl={6}>
													<Radio value={USE_OF_FUND.REFINANCE_SELF_USE}>
														{t(
															i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.value.REFINANCE_SELF_USE
														)}
													</Radio>
												</Col>
												<Col xs={12} sm={12} md={12} lg={12} xl={6} xxl={6}>
													<Radio value={USE_OF_FUND.REFINANCE_BTL}>
														{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.useOfFunds.value.REFINANCE_BTL)}
													</Radio>
												</Col>
											</Row>
										</Col>
										<Col xs={24} sm={24} md={24} lg={24} xl={5} xxl={7}>
											{renderOtherUseOfFunds()}
										</Col>
									</Row>
								</Radio.Group>
							</Form.Item>
						</Row>
					</div>
					<TitleBox>
						<span>{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.securityDetails)}</span>
					</TitleBox>
					<div className={styles.subForm}>
						<Row gutter={20}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.propertyType)}`}
									name={['securityDetails', 'propertyType']}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Select
										options={OptionPropertyType}
										placeholder={`${t(i18nKey.common.select)}`}
										onChange={onChangePropertyType}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name={['securityDetails', 'noOfBedroom']}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.noOfBedroom)}`}>
									<Select
										allowClear
										options={numberOfBedroomOption}
										disabled={!isDisabledNumberOfBedRoom}
										placeholder={`${t(i18nKey.common.select)}`}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={20}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name={['securityDetails', 'status']}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.status)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Radio.Group>
										<Row>
											<Radio value={'vacant'}>{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.vacant)}</Radio>
											<Radio value={'tenanted'}>
												{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.tenanted)}
											</Radio>
										</Row>
									</Radio.Group>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									name={['securityDetails', 'condition']}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.condition)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Select options={conditionOption} placeholder={`${t(i18nKey.common.select)}`} />
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<Typography.Title level={3}>
									{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.address)}*
								</Typography.Title>
							</Col>
							<Col span={24} className={styles.dividerLine}></Col>
						</Row>
						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col span={24}>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['securityDetails', 'addressLine']}
												label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}
												required={true}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['securityDetails', 'street']}
												label={`${t(i18nKey.dashboard.accountInformation.street)}`}
												required={true}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row
										gutter={{
											sm: 6,
											xs: 6,
											md: 32
										}}>
										<Col xs={24} md={12} sm={24}>
											<Form.Item
												name={['securityDetails', 'country']}
												label={`${t(i18nKey.dashboard.accountInformation.country)}`}
												required={true}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Select
													options={applicationStore.countriesOptions}
													showSearch
													filterOption={(input, option) =>
														new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
													}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12} sm={24}>
											<Form.Item
												name={['securityDetails', 'district']}
												label={`${t(i18nKey.dashboard.accountInformation.district)}`}
												required={true}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Select options={listProvinceSecurityDetailsOptions} />
											</Form.Item>
										</Col>
									</Row>
									{mortgageDealDetailsStore.mortgageDealForm.securityDetails?.country !==
										HONG_KONG_NATIONALITY_VALUE && (
										<>
											<Row gutter={[32, 16]}>
												<Col span={24}>
													<Form.Item
														name={['securityDetails', 'zipCode']}
														label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
														required={true}
														rules={[
															{
																required: true,
																message: `${t(i18nKey.onboarding.validation.required)}`
															},
															{
																pattern: ZIPCODE_REGEX,
																message: `${t(i18nKey.onboarding.validation.zipCode)}`
															}
														]}>
														<Input />
													</Form.Item>
												</Col>
											</Row>
										</>
									)}
								</Col>
							</Row>
							<Row gutter={20}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										required={true}
										label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.purchasePrice)}`}>
										<Row gutter={[16, 16]}>
											<Col span={6}>
												<Form.Item noStyle name={['securityDetails', 'purchaseCurrency']}>
													<Select
														options={[
															{
																key: ONBOARDING_CURRENCY_TYPE.HKD,
																value: ONBOARDING_CURRENCY_TYPE.HKD,
																label: ONBOARDING_CURRENCY_TYPE.HKD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.USD,
																value: ONBOARDING_CURRENCY_TYPE.USD,
																label: ONBOARDING_CURRENCY_TYPE.USD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.GBP,
																value: ONBOARDING_CURRENCY_TYPE.GBP,
																label: ONBOARDING_CURRENCY_TYPE.GBP
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.JPY,
																value: ONBOARDING_CURRENCY_TYPE.JPY,
																label: ONBOARDING_CURRENCY_TYPE.JPY
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.AUD,
																value: ONBOARDING_CURRENCY_TYPE.AUD,
																label: ONBOARDING_CURRENCY_TYPE.AUD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.CAD,
																value: ONBOARDING_CURRENCY_TYPE.CAD,
																label: ONBOARDING_CURRENCY_TYPE.CAD
															}
														]}
													/>
												</Form.Item>
											</Col>
											<Col span={18}>
												<Form.Item
													name={['securityDetails', 'purchasePrice']}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
													normalize={(c: string, p) => {
														if (!c) {
															return '';
														}
														c = c.replaceAll(',', '');
														const newValue = Number(c);

														if (isNaN(newValue) || newValue > DEFAULT_INPUT_MAX_NUMBER) {
															return p || '';
														}
														return newValue.toLocaleString('en-US');
													}}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										required={true}
										label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.currentOrExpectedRentPerMonth)}`}>
										<Row gutter={[16, 16]}>
											<Col span={6}>
												<Form.Item noStyle name={['securityDetails', 'currentOrExpectedCurrency']}>
													<Select
														options={[
															{
																key: ONBOARDING_CURRENCY_TYPE.HKD,
																value: ONBOARDING_CURRENCY_TYPE.HKD,
																label: ONBOARDING_CURRENCY_TYPE.HKD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.USD,
																value: ONBOARDING_CURRENCY_TYPE.USD,
																label: ONBOARDING_CURRENCY_TYPE.USD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.GBP,
																value: ONBOARDING_CURRENCY_TYPE.GBP,
																label: ONBOARDING_CURRENCY_TYPE.GBP
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.JPY,
																value: ONBOARDING_CURRENCY_TYPE.JPY,
																label: ONBOARDING_CURRENCY_TYPE.JPY
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.AUD,
																value: ONBOARDING_CURRENCY_TYPE.AUD,
																label: ONBOARDING_CURRENCY_TYPE.AUD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.CAD,
																value: ONBOARDING_CURRENCY_TYPE.CAD,
																label: ONBOARDING_CURRENCY_TYPE.CAD
															}
														]}
													/>
												</Form.Item>
											</Col>
											<Col span={18}>
												<Form.Item
													name={['securityDetails', 'currentOrExpectedRentPerMonth']}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
													normalize={(c: string, p) => {
														if (!c) {
															return '';
														}
														c = c.replaceAll(',', '');
														const newValue = Number(c);

														if (isNaN(newValue) || newValue > DEFAULT_INPUT_MAX_NUMBER) {
															return p || '';
														}
														return newValue.toLocaleString('en-US');
													}}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={20}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										required={true}
										label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.groundRentPerMonth)}`}>
										<Row gutter={[16, 16]}>
											<Col span={6}>
												<Form.Item noStyle name={['securityDetails', 'groundRentPerMonthCurrency']}>
													<Select
														options={[
															{
																key: ONBOARDING_CURRENCY_TYPE.HKD,
																value: ONBOARDING_CURRENCY_TYPE.HKD,
																label: ONBOARDING_CURRENCY_TYPE.HKD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.USD,
																value: ONBOARDING_CURRENCY_TYPE.USD,
																label: ONBOARDING_CURRENCY_TYPE.USD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.GBP,
																value: ONBOARDING_CURRENCY_TYPE.GBP,
																label: ONBOARDING_CURRENCY_TYPE.GBP
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.JPY,
																value: ONBOARDING_CURRENCY_TYPE.JPY,
																label: ONBOARDING_CURRENCY_TYPE.JPY
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.AUD,
																value: ONBOARDING_CURRENCY_TYPE.AUD,
																label: ONBOARDING_CURRENCY_TYPE.AUD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.CAD,
																value: ONBOARDING_CURRENCY_TYPE.CAD,
																label: ONBOARDING_CURRENCY_TYPE.CAD
															}
														]}
													/>
												</Form.Item>
											</Col>
											<Col span={18}>
												<Form.Item
													name={['securityDetails', 'groundRentPerMonth']}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
													normalize={(c: string, p) => {
														if (!c) {
															return '';
														}
														c = c.replaceAll(',', '');
														const newValue = Number(c);

														if (isNaN(newValue) || newValue > DEFAULT_INPUT_MAX_NUMBER) {
															return p || '';
														}
														return newValue.toLocaleString('en-US');
													}}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										required={true}
										label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.buildingManagementFeePerMonth)}`}>
										<Row gutter={[16, 16]}>
											<Col span={6}>
												<Form.Item noStyle name={['securityDetails', 'buildingManagementFeePerMonthCurrency']}>
													<Select
														options={[
															{
																key: ONBOARDING_CURRENCY_TYPE.HKD,
																value: ONBOARDING_CURRENCY_TYPE.HKD,
																label: ONBOARDING_CURRENCY_TYPE.HKD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.USD,
																value: ONBOARDING_CURRENCY_TYPE.USD,
																label: ONBOARDING_CURRENCY_TYPE.USD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.GBP,
																value: ONBOARDING_CURRENCY_TYPE.GBP,
																label: ONBOARDING_CURRENCY_TYPE.GBP
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.JPY,
																value: ONBOARDING_CURRENCY_TYPE.JPY,
																label: ONBOARDING_CURRENCY_TYPE.JPY
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.AUD,
																value: ONBOARDING_CURRENCY_TYPE.AUD,
																label: ONBOARDING_CURRENCY_TYPE.AUD
															},
															{
																key: ONBOARDING_CURRENCY_TYPE.CAD,
																value: ONBOARDING_CURRENCY_TYPE.CAD,
																label: ONBOARDING_CURRENCY_TYPE.CAD
															}
														]}
													/>
												</Form.Item>
											</Col>
											<Col span={18}>
												<Form.Item
													name={['securityDetails', 'buildingManagementFeePerMonth']}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
													normalize={(c: string, p) => {
														if (!c) {
															return '';
														}
														c = c.replaceAll(',', '');
														const newValue = Number(c);

														if (isNaN(newValue) || newValue > DEFAULT_INPUT_MAX_NUMBER) {
															return p || '';
														}
														return newValue.toLocaleString('en-US');
													}}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={20}>
								<Col xs={24} sm={24} md={24} lg={9}>
									<Form.Item
										name={['securityDetails', 'holdingType']}
										label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.holdingType)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Radio.Group>
											<Row>
												<Radio value={holdingTypeOptions.FREE_HOLD}>
													{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.freeHold)}
												</Radio>
												<Radio value={holdingTypeOptions.LEASE_HOLD}>
													{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.leaseHold)}
												</Radio>
											</Row>
										</Radio.Group>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										required={true}
										label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.unexpiredTermOfLease)}`}>
										<div className={styles.horizontal}>
											<div className={styles.inlineSelect}>
												<NumberInput
													name={['securityDetails', 'unexpiredTermOfLease']}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
												/>
											</div>
											<Typography.Text strong>
												{t(i18nKey.onboarding.request.loanLendingCriteria.months)}
											</Typography.Text>
										</div>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[8, 8]}>
								<Form.Item
									style={{ width: '100%' }}
									name={['securityDetails', 'ownership']}
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.ownership.title)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Radio.Group className={styles.RadioSpace} onChange={onChangeOwnership}>
										<Row gutter={[0, 10]}>
											<Col xs={24} sm={24} md={24} lg={24} xl={17} xxl={17}>
												<Row>
													<Col span={8}>
														<Radio value={OWNERSHIP.JOIN_TENANCY}>
															{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.ownership.jointTenancy)}
														</Radio>
													</Col>
													<Col span={8}>
														<Radio value={OWNERSHIP.TENANCY_IN_COMMON}>
															{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.ownership.tenancyInCommon)}
														</Radio>
													</Col>
													<Col span={8}>
														<Radio value={OWNERSHIP.INDIVIDUAL_OWNERSHIP}>
															{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.ownership.individualOwnership)}
														</Radio>
													</Col>
												</Row>
											</Col>
											<Col xs={24} sm={24} md={24} lg={24} xl={7} xxl={7}>
												{renderOwnership()}
											</Col>
										</Row>
									</Radio.Group>
								</Form.Item>
							</Row>
							<Row>
								<Col xs={19} sm={19} md={19} className={styles.questionBox}>
									<span className="ant-form-item-label">
										{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.question1)}
										{''}
										<span className={styles.requireAsterisk}>*</span>
										<Popover
											title={null}
											trigger="click"
											content={t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.tooltipQuestion1)}
											placement="bottom">
											<ExclamationCircleFilled className={styles.iconRequire} />
										</Popover>
									</span>
								</Col>
								<Col xs={5} sm={5} md={5}>
									<Form.Item
										className={styles.answerBox}
										name={['securityDetails', 'mortgageApprovedBySecondBankQuestion']}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Radio.Group options={yesNoOptions} />
									</Form.Item>
								</Col>
							</Row>
							{mortgageDealDetailsStore.mortgageDealForm.securityDetails?.mortgageApprovedBySecondBankQuestion ===
								BORROWER_CREDIT_VALUE.YES && (
								<Row>
									<Col span={24}>
										<Form.Item
											name={['securityDetails', 'mortgageApprovedBySecondBankQuestionNote']}
											rules={[
												{
													required: true,
													message: `${t(i18nKey.onboarding.validation.required)}`
												}
											]}>
											<Input.TextArea
												style={{ height: 140, resize: 'none' }}
												placeholder={`${t(
													i18nKey.onboarding.request.otherDetails.individual.otherFill
												)}`}></Input.TextArea>
										</Form.Item>
									</Col>
								</Row>
							)}
							<Row>
								<Col xs={19} sm={19} md={19} className={styles.questionBox}>
									<span className={styles.questionText}>
										{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.question2)}
										<span className={styles.requireAsterisk}>*</span>
									</span>
								</Col>
								<Col xs={5} sm={5} md={5}>
									<Form.Item
										className={styles.answerBox}
										name={['securityDetails', 'isSecondMortgageForSecurityQuestion']}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Radio.Group options={yesNoOptions} />
									</Form.Item>
								</Col>
							</Row>
							{mortgageDealDetailsStore.mortgageDealForm.securityDetails?.isSecondMortgageForSecurityQuestion ===
								BORROWER_CREDIT_VALUE.YES && (
								<Row>
									<Col span={24}>
										<Form.Item
											name={['securityDetails', 'isSecondMortgageForSecurityQuestionNote']}
											rules={[
												{
													required: true,
													message: `${t(i18nKey.onboarding.validation.required)}`
												}
											]}>
											<Input.TextArea
												style={{ height: 140, resize: 'none' }}
												placeholder={`${t(
													i18nKey.onboarding.request.otherDetails.individual.otherFill
												)}`}></Input.TextArea>
										</Form.Item>
									</Col>
								</Row>
							)}
						</div>
					</div>
					<TitleBox>
						<span>{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.solicitorDetails)}</span>
					</TitleBox>
					<div className={styles.subForm}>
						<Row>
							<Col xs={24} sm={24} md={12}>
								<Form.Item
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyName)}`}
									name={['solicitorDetails', 'companyName']}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<Typography.Title level={3}>
									{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyAddress)}
								</Typography.Title>
							</Col>
							<Col span={24} className={styles.dividerLine}></Col>
						</Row>
						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col span={24}>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['solicitorDetails', 'addressLine']}
												label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['solicitorDetails', 'street']}
												label={`${t(i18nKey.dashboard.accountInformation.street)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row
										gutter={{
											sm: 6,
											xs: 6,
											md: 32
										}}>
										<Col xs={24} md={12} sm={24}>
											<Form.Item
												name={['solicitorDetails', 'country']}
												label={`${t(i18nKey.dashboard.accountInformation.country)}`}
												required={false}
												rules={[
													{
														required: false,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Select
													options={applicationStore.listOfCountry}
													showSearch
													filterOption={(input, option) =>
														new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
													}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12} sm={24}>
											<Form.Item
												name={['solicitorDetails', 'district']}
												label={`${t(i18nKey.dashboard.accountInformation.district)}`}
												required={false}
												rules={[
													{
														required: false,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									{mortgageDealDetailsStore.mortgageDealForm.solicitorDetails?.country !==
										HONG_KONG_NATIONALITY_VALUE && (
										<>
											<Row gutter={[32, 16]}>
												<Col span={24}>
													<Form.Item
														name={['solicitorDetails', 'zipCode']}
														label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
														rules={[
															{
																pattern: ZIPCODE_REGEX,
																message: `${t(i18nKey.onboarding.validation.zipCode)}`
															}
														]}>
														<Input />
													</Form.Item>
												</Col>
											</Row>
										</>
									)}
									<Row gutter={[32, 16]}>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Form.Item
												name={['solicitorDetails', 'contactPerson']}
												label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.contactPerson)}`}>
												<Input />
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Form.Item label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.contactNumber)}`}>
												<Row gutter={[16, 16]}>
													<Col xs={9} sm={9} md={6} lg={11} xl={9} xxl={7}>
														<Form.Item noStyle name={['solicitorDetails', 'contactCountryCode']}>
															<Select
																placeholder={`${t(i18nKey.common.select)}`}
																options={applicationStore.phoneCountryCode}
																showSearch
																filterOption={(input, option) =>
																	(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
																}
															/>
														</Form.Item>
													</Col>
													<Col xs={15} sm={15} md={18} lg={13} xl={15} xxl={17}>
														<NumberInput name={['solicitorDetails', 'contactPhoneNumber']} />
													</Col>
												</Row>
											</Form.Item>
										</Col>
									</Row>
									<Row>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Form.Item
												rules={[
													{
														pattern: EMAIL_VALIDATION_RULES.REGEX,
														message: `${t(i18nKey.registration.validation.wrongEmailFormat)}`
													}
												]}
												name={['solicitorDetails', 'emailAddress']}
												label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.emailAddress)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</Col>
							</Row>
						</div>
					</div>
					<TitleBox>
						<span>{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.propertyCompanyManagement)}</span>
					</TitleBox>
					<div className={styles.subForm}>
						<Row>
							<Col xs={24} sm={24} md={12}>
								<Form.Item
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyName)}`}
									name={['propertyCompanyManagement', 'companyName']}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row>
							<Col span={24}>
								<Typography.Title level={3}>
									{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyAddress)}
								</Typography.Title>
							</Col>
							<Col span={24} className={styles.dividerLine}></Col>
						</Row>
						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col span={24}>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['propertyCompanyManagement', 'addressLine']}
												label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['propertyCompanyManagement', 'street']}
												label={`${t(i18nKey.dashboard.accountInformation.street)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row
										gutter={{
											sm: 6,
											xs: 6,
											md: 32
										}}>
										<Col xs={24} md={12} sm={24}>
											<Form.Item
												name={['propertyCompanyManagement', 'country']}
												label={`${t(i18nKey.dashboard.accountInformation.country)}`}
												required={false}
												rules={[
													{
														required: false,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Select placeholder="Select" options={applicationStore.listOfCountry}
													showSearch
													filterOption={(input, option) =>
														new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
													}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12} sm={24}>
											<Form.Item
												name={['propertyCompanyManagement', 'district']}
												label={`${t(i18nKey.dashboard.accountInformation.district)}`}
												required={false}
												rules={[
													{
														required: false,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									{mortgageDealDetailsStore.mortgageDealForm.propertyCompanyManagement?.country !==
										HONG_KONG_NATIONALITY_VALUE && (
										<>
											<Row gutter={[32, 16]}>
												<Col span={24}>
													<Form.Item
														name={['propertyCompanyManagement', 'zipCode']}
														label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
														rules={[
															{
																pattern: ZIPCODE_REGEX,
																message: `${t(i18nKey.onboarding.validation.zipCode)}`
															}
														]}>
														<Input />
													</Form.Item>
												</Col>
											</Row>
										</>
									)}
									<Row gutter={[32, 16]}>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Form.Item
												name={['propertyCompanyManagement', 'contactPerson']}
												label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.contactPerson)}`}>
												<Input />
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Form.Item label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.contactNumber)}`}>
												<Row gutter={[16, 16]}>
													<Col xs={9} sm={9} md={6} lg={11} xl={9} xxl={7}>
														<Form.Item noStyle name={['propertyCompanyManagement', 'contactCountryCode']}>
															<Select
																placeholder={`${t(i18nKey.common.select)}`}
																options={applicationStore.phoneCountryCode}
																showSearch
																filterOption={(input, option) =>
																	(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
																}
															/>
														</Form.Item>
													</Col>
													<Col xs={15} sm={15} md={18} lg={13} xl={15} xxl={17}>
														<NumberInput name={['propertyCompanyManagement', 'contactPhoneNumber']} />
													</Col>
												</Row>
											</Form.Item>
										</Col>
									</Row>
									<Row>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Form.Item
												rules={[
													{
														pattern: EMAIL_VALIDATION_RULES.REGEX,
														message: `${t(i18nKey.registration.validation.wrongEmailFormat)}`
													}
												]}
												name={['propertyCompanyManagement', 'emailAddress']}
												label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.emailAddress)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</Col>
							</Row>
						</div>
					</div>
					<TitleBox>
						<span style={{ marginRight: 5 }}>
							{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.thirdPartyReferral)}
						</span>
						<Popover
							title={null}
							trigger="click"
							content={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.tooltipThirdPartyReferral)}`}
							placement="top">
							<ExclamationCircleFilled className={styles.iconRequire} />
						</Popover>
					</TitleBox>
					<div className={styles.subForm}>
						<Row>
							<Col xs={24} sm={24} md={24} lg={24}>
								<Form.Item
									name="doYouHaveAReferral"
									label={`${t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.doYouHaveAReferral)}`}
									required={true}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										}
									]}>
									<Radio.Group style={{ width: '100%' }} onChange={onChangeReferral}>
										<Row style={{ width: '100%' }}>
											<Col xs={12} sm={12} md={12} lg={4}>
												<Radio value={'yes'}>{t(i18nKey.common.yes)}</Radio>
											</Col>
											<Col xs={12} sm={12} md={12} lg={4}>
												<Radio value={'no'}>{t(i18nKey.common.no)}</Radio>
											</Col>
										</Row>
									</Radio.Group>
								</Form.Item>
							</Col>
						</Row>
					</div>
					{mortgageDealDetailsStore.mortgageDealForm.doYouHaveAReferral === 'yes' && (
						<>
							<Row>
								<div className={styles.box}>
									<Row>
										<Col xs={21} sm={21} md={23}>
											<Typography.Title level={3}>
												{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.referralList)}
											</Typography.Title>
										</Col>
										{!isViewFullForm && (
											<Col xs={3} sm={3} md={1}>
												<Button
													type="primary"
													icon={<PlusOutlined />}
													onClick={() => {
														if (addReferralBtn && addReferralBtn.current) {
															addReferralBtn.current.click();
														}
													}}></Button>
											</Col>
										)}
									</Row>
									<Row gutter={[10, 20]} style={{ marginBottom: 10 }}>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Row>
												<Typography.Title level={5}>
													{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyNameOrIndividualName)}
													<span className={styles.requireAsterisk}>*</span>
												</Typography.Title>
											</Row>
											<Row className={styles.formBox}>
												<Form.Item
													name={['mainReferral', 'companyNameOrIndividualName']}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
													style={{ width: '100%' }}>
													<Input />
												</Form.Item>
											</Row>
										</Col>
										<Col xs={24} sm={24} md={24} lg={12}>
											<Row>
												<Typography.Title level={5}>
													{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.address)}
													<span className={styles.requireAsterisk}>*</span>
												</Typography.Title>
											</Row>
											<Row className={styles.formBox}>
												<Form.Item
													name={['mainReferral', 'address']}
													style={{ width: '100%' }}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}>
													<Input />
												</Form.Item>
											</Row>
										</Col>
									</Row>
									<Form.List name={['thirdPartyReferral', 'referralList']}>
										{(fields, { add }) => (
											<>
												<span
													ref={addReferralBtn}
													onClick={() => {
														if (fields.length < 2) {
															add();
														}
													}}></span>
												{fields.map((f, idx) => (
													<Row gutter={[10, 20]} key={f.key} style={{ marginBottom: 10 }}>
														<Col xs={24} sm={24} md={24} lg={12}>
															<Row>
																<Typography.Title level={5}>
																	{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyNameOrIndividualName)}
																	<span className={styles.requireAsterisk}>*</span>
																</Typography.Title>
															</Row>
															<Row className={styles.formBox}>
																<Form.Item
																	name={[idx, 'companyNameOrIndividualName']}
																	style={{ width: '100%' }}
																	required={false}
																	rules={[
																		{
																			required: true,
																			message: `${t(i18nKey.onboarding.validation.required)}`
																		}
																	]}>
																	<Input />
																</Form.Item>
															</Row>
														</Col>
														<Col xs={24} sm={24} md={24} lg={12}>
															<Row>
																<Typography.Title level={5}>
																	{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.address)}
																	<span className={styles.requireAsterisk}>*</span>
																</Typography.Title>
															</Row>
															<Row className={styles.formBox}>
																<Form.Item
																	name={[idx, 'address']}
																	style={{ width: '100%' }}
																	required={false}
																	rules={[
																		{
																			required: true,
																			message: `${t(i18nKey.onboarding.validation.required)}`
																		}
																	]}>
																	<Input />
																</Form.Item>
															</Row>
														</Col>
													</Row>
												))}
											</>
										)}
									</Form.List>
								</div>
							</Row>
						</>
					)}
				</Form>
				{!isViewFullForm && (
					<Row
						gutter={[
							{ xs: 0, sm: 0, md: 0, lg: 132 },
							{ xs: 20, sm: 20, md: 20, lg: 16 }
						]}
						style={{ marginTop: 30 }}>
						<Col span={24}>
							<Row gutter={16}>
								<Col xs={5} sm={5} md={5} lg={2}>
									<Button block type="primary" onClick={onBack}>
										<LeftOutlined />
									</Button>
								</Col>
								<Col xs={19} sm={19} md={19} lg={22}>
									<Button block type="primary" onClick={onSaveClick}>
										{t(i18nKey.onboarding.saveAndNext)}
									</Button>
								</Col>
							</Row>
						</Col>
					</Row>
				)}
			</Spin>
		</>
	);
};

export default observer(MortgageDealDetails);
